import React, { useState } from 'react';
import { Routes, Route, Navigate, NavLink, Link } from 'react-router-dom';
import {
  LogOut, Wrench, Users, CalendarDays, Home as HomeIcon,
  Settings as SettingsIcon, ClipboardCheck, Shield, CloudOff,
  MessageSquarePlus, Inbox, AlertTriangle,
} from 'lucide-react';
import { IS_SANDBOX } from './firebase.js';
import { useAuth } from './lib/useAuth.js';
import { useOnline } from './lib/use-online.js';
import { useBusinessProfile } from './lib/use-business-profile.js';
import Home from './Home.jsx';
import Customers from './Customers.jsx';
import Jobs from './Jobs.jsx';
import Calendar from './Calendar.jsx';
import Settings from './Settings.jsx';
import Outstanding from './Outstanding.jsx';
import History from './History.jsx';
import ActiveJob from './ActiveJob.jsx';
import ActiveJobBanner from './ActiveJobBanner.jsx';
import PublicShare from './PublicShare.jsx';
import PublicInvoice from './PublicInvoice.jsx';
import WhatsNewModal from './WhatsNewModal.jsx';
import Earnings from './Earnings.jsx';
import AdminUsers from './AdminUsers.jsx';
import AdminFeedback from './AdminFeedback.jsx';
import AdminErrors from './AdminErrors.jsx';
import FeedbackModal from './FeedbackModal.jsx';

// Build identifier shown in the header. Surfaced for parity with Brad's
// other apps (LAMP shows v2026.0429.26 etc.) — useful when triaging
// "is the user on the latest version?"
// Defined in version.js so crash reports can stamp it too.
import { APP_VERSION } from './version.js';

// Tells the mechanic what's actually happening when signal drops.
// Without it, offline looks identical to "the app is broken" — the
// data is all there from the local cache, but a save that hasn't
// reached the server yet is invisible and unexplained.
function OfflineBanner() {
  const online = useOnline();
  if (online) return null;
  return (
    <div className="offline-banner" role="status">
      <CloudOff size={14} />
      <span>
        <strong>Offline</strong> — you can keep working. Changes save to this device and
        sync when you're back in signal.
      </span>
    </div>
  );
}

function SandboxBanner() {
  if (!IS_SANDBOX) return null;
  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
        background: 'repeating-linear-gradient(45deg, #f59e0b, #f59e0b 12px, #111 12px, #111 24px)',
        color: '#fff', textAlign: 'center', fontSize: 11, fontWeight: 800, letterSpacing: 2,
        padding: '3px 8px', textTransform: 'uppercase', borderBottom: '2px solid #f59e0b',
        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
      }}
    >
      ⚠ Sandbox Environment · Data is separate from production ⚠
    </div>
  );
}

function SignInScreen({ signIn, signInWithEmail, signUpWithEmail, resetPassword, error }) {
  // 'in' (default) or 'up' — same form, different action.
  const [mode, setMode] = useState('in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // 'idle' while signing in, 'asking' on the reset form, 'sent' after.
  const [resetState, setResetState] = useState('idle');

  const isSignUp = mode === 'up';

  async function handleEmailSubmit(e) {
    e.preventDefault();
    if (!email || !password) return;
    if (isSignUp && password.length < 6) {
      // Firebase rejects passwords under 6 chars — surface that earlier.
      alert('Pick a password at least 6 characters long.');
      return;
    }
    setSubmitting(true);
    try {
      if (isSignUp) await signUpWithEmail(email, password);
      else          await signInWithEmail(email, password);
    } catch (_) { /* error surfaced via useAuth */ }
    finally { setSubmitting(false); }
  }

  async function handleReset(e) {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await resetPassword(email);
      setResetState('sent');
    } catch (_) { /* surfaced via useAuth */ }
    finally { setSubmitting(false); }
  }

  // Forgetting a password shouldn't mean losing access to your own
  // business records, so this is its own screen rather than a link
  // to somewhere else.
  if (resetState !== 'idle') {
    return (
      <div className="signin-shell">
        <SandboxBanner />
        <div className="signin-card">
          <div className="signin-brand">
            <img src="/bayless-logo.jpg" alt="Bayless" className="signin-logo" />
            <p>{resetState === 'sent' ? 'Check your email' : 'Reset your password'}</p>
          </div>

          {resetState === 'sent' ? (
            <>
              {/* Deliberately the same message whether or not that
                  address has an account. Saying "no account found"
                  would let anyone test who has a Bayless login. */}
              <p className="signin-reset-sent">
                If <strong>{email}</strong> has an account, a link to set a new password is
                on its way. It's good for one hour.
              </p>
              <p className="muted small">
                Nothing arrived? Check spam, and make sure you typed the address you signed
                up with.
              </p>
            </>
          ) : (
            <form onSubmit={handleReset} className="form">
              <p className="muted small">
                Enter your email and we'll send you a link to set a new one.
              </p>
              <input
                type="email"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
              <button type="submit" className="btn" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send the link'}
              </button>
            </form>
          )}

          {error && <p className="form-error" style={{ marginTop: 12 }}>{error.message}</p>}

          <p className="signin-switch">
            <button type="button" className="btn-link" onClick={() => setResetState('idle')}>
              Back to sign in
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="signin-shell">
      <SandboxBanner />
      <div className="signin-card">
        <div className="signin-brand">
          <img src="/bayless-logo.jpg" alt="Bayless" className="signin-logo" />
          <p>{isSignUp ? 'Create your account to get started' : 'Sign in to continue'}</p>
        </div>

        {/* Sign in / Sign up toggle — same form, two actions. */}
        <div className="signin-tabs" role="tablist" aria-label="Sign in or sign up">
          <button
            type="button"
            role="tab"
            aria-selected={!isSignUp}
            className={`signin-tab ${!isSignUp ? 'active' : ''}`}
            onClick={() => setMode('in')}
          >
            Sign in
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isSignUp}
            className={`signin-tab ${isSignUp ? 'active' : ''}`}
            onClick={() => setMode('up')}
          >
            Sign up
          </button>
        </div>

        <button className="btn" onClick={signIn} style={{ width: '100%' }}>
          {isSignUp ? 'Sign up with Google' : 'Sign in with Google'}
        </button>

        <div className="signin-divider">or</div>

        <form onSubmit={handleEmailSubmit} className="form">
          <div className="form-row">
            <span>Email</span>
            <input
              type="email" autoComplete="username" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>
          <div className="form-row">
            <span>Password</span>
            <input
              type="password"
              autoComplete={isSignUp ? 'new-password' : 'current-password'}
              required
              value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder={isSignUp ? 'At least 6 characters' : '••••••••'}
            />
          </div>
          <button type="submit" className="btn-secondary" disabled={submitting} style={{ width: '100%' }}>
            {submitting
              ? (isSignUp ? 'Creating account…' : 'Signing in…')
              : (isSignUp ? 'Create account with email' : 'Sign in with email')}
          </button>
        </form>

        {!isSignUp && (
          <p className="signin-forgot">
            <button type="button" className="btn-link" onClick={() => setResetState('asking')}>
              Forgot your password?
            </button>
          </p>
        )}

        <p className="signin-switch">
          {isSignUp
            ? <>Already have an account? <button type="button" className="btn-link" onClick={() => setMode('in')}>Sign in</button></>
            : <>New here? <button type="button" className="btn-link" onClick={() => setMode('up')}>Create an account</button></>
          }
        </p>

        {error && (
          <p className="form-error" style={{ marginTop: 12 }}>
            {error.message}
          </p>
        )}

      </div>
    </div>
  );
}

// Brand strip in the top-left of the header. Pulled into a child
// component so useBusinessProfile() only subscribes AFTER the auth
// gate confirms a user — calling it from AuthGatedApp directly would
// fire businessProfileDoc() which throws when not signed in.
//
// Layout: 48px logo block on the left, title + "Signed in as" subtitle
// stacked on the right. Big enough to actually read the logo design,
// while still leaving the version pill + nav controls room on the
// right side of the header.
function HeaderBrand({ user }) {
  const { profile } = useBusinessProfile();
  return (
    <>
      <div className="header-logo-block">
        {profile.logoUrl ? (
          <img src={profile.logoUrl} alt={profile.name || 'Business logo'} />
        ) : (
          <Wrench size={26} color="var(--accent)" strokeWidth={2.5} />
        )}
      </div>
      <div className="app-header-identity">
        <div className="title">
          {profile.name || 'Bayless'}
        </div>
        <div className="subtitle">
          Signed in as {user.displayName || user.email}
        </div>
      </div>
    </>
  );
}

// Everything behind the sign-in wall — the mechanic's workspace.
function AuthGatedApp() {
  const {
    user, ready, error, isAdmin,
    signIn, signInWithEmail, signUpWithEmail, signOut, resetPassword,
  } = useAuth();
  // Reachable from every screen on purpose. Feedback you have to go
  // looking for is feedback you don't get.
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  if (!ready) {
    return (
      <div className="shell">
        <SandboxBanner />
        <OfflineBanner />
        <div className="center">
          <p className="muted">Loading…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <SignInScreen
        signIn={signIn}
        signInWithEmail={signInWithEmail}
        signUpWithEmail={signUpWithEmail}
        resetPassword={resetPassword}
        error={error}
      />
    );
  }

  return (
    <div className="shell">
      <SandboxBanner />
      <OfflineBanner />
      <WhatsNewModal />
      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
      <ActiveJobBanner />
      <header className="app-header">
        <div className="app-header-left">
          <HeaderBrand user={user} />
        </div>
        <div className="app-header-right">
          <div className="app-header-meta">
            <span className="version-pill">{APP_VERSION}</span>
            <span className="powered-by">
              <img src="/favicon.png?v=8" alt="" className="powered-by-mark" />
              Powered by Bayless
            </span>
          </div>
          <button
            type="button"
            className="btn-link"
            onClick={() => setFeedbackOpen(true)}
            aria-label="Send feedback"
            title="Send feedback or request a feature"
          >
            <MessageSquarePlus size={16} />
          </button>
          <Link to="/settings" className="btn-link" aria-label="Settings" title="Settings">
            <SettingsIcon size={16} />
          </Link>
          <button className="btn-link" onClick={signOut} aria-label="Sign out">
            <LogOut size={16} />
            <span style={{ display: 'none' }} className="hide-on-narrow">Sign out</span>
          </button>
        </div>
      </header>
      <nav className="app-tabs">
        <NavLink to="/" end className={({ isActive }) => `app-tab ${isActive ? 'active' : ''}`}>
          <HomeIcon size={15} /> Home
        </NavLink>
        <NavLink to="/customers" className={({ isActive }) => `app-tab ${isActive ? 'active' : ''}`}>
          <Users size={15} /> Customers
        </NavLink>
        <NavLink to="/calendar" className={({ isActive }) => `app-tab ${isActive ? 'active' : ''}`}>
          <CalendarDays size={15} /> Calendar
        </NavLink>
        <NavLink to="/history" className={({ isActive }) => `app-tab ${isActive ? 'active' : ''}`}>
          <ClipboardCheck size={15} /> History
        </NavLink>
        {isAdmin && (
          <NavLink to="/admin" end className={({ isActive }) => `app-tab ${isActive ? 'active' : ''}`}>
            <Shield size={15} /> Admin
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/admin/feedback" className={({ isActive }) => `app-tab ${isActive ? 'active' : ''}`}>
            <Inbox size={15} /> Feedback
          </NavLink>
        )}
        {isAdmin && (
          <NavLink to="/admin/errors" className={({ isActive }) => `app-tab ${isActive ? 'active' : ''}`}>
            <AlertTriangle size={15} /> Crashes
          </NavLink>
        )}
      </nav>
      <main className="app-main">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/customers" element={<Customers />}>
            <Route path=":customerId" element={<Jobs />} />
          </Route>
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/outstanding" element={<Outstanding />} />
          <Route path="/earnings" element={<Earnings />} />
          <Route path="/history" element={<History />} />
          <Route path="/active/:jobId" element={<ActiveJob />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/admin" element={isAdmin ? <AdminUsers /> : <Navigate to="/" replace />} />
          <Route path="/admin/feedback" element={isAdmin ? <AdminFeedback /> : <Navigate to="/" replace />} />
          <Route path="/admin/errors" element={isAdmin ? <AdminErrors /> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/share/:shareId" element={<PublicShare />} />
      <Route path="/invoice/:shareId" element={<PublicInvoice />} />
      <Route path="/*" element={<AuthGatedApp />} />
    </Routes>
  );
}
